/**
 * src/ui/jugadores-modal.js — modal "Jugadores del grupo" (26-may-2026 noche).
 *
 * Disparado desde el subtítulo del header (`renderActiveGroupBar` en
 * `src/ui/components.js`) cuando el usuario clickea "👥 Ver jugadores · N".
 *
 * Modal solo lectura: lista de jugadores del grupo activo ordenada por
 * fecha de ingreso ASC (FIFO). Cada fila: apodo (display_name) grande +
 * nombre real (usuarios.nombre_real) tenue + 👑 si is_admin. Sin email,
 * sin puntos, sin acciones, sin navegación.
 *
 * Lee del cache local (getGrupoDeCache vía getNormalizedDB) — el cache
 * ya está hidratado por cargarMisGrupos al login y trae el shape
 * adaptarJugador (display_name, nombre_real, is_admin, joined_at).
 * No hace fetch.
 *
 * Bridge a window.openJugadoresModal porque los `onclick=""` de
 * `components.js` lo invocan desde 6 pantallas distintas (mismo patrón
 * que window.openGroupSelector / window.shareActiveGroup).
 */

import { getNormalizedDB } from '../core/db.js';
import { getGroupDisplayName } from '../core/groups.js';

const MODAL_ID = 'jugadoresModal';

// HTML puro del modal — exportado para test. Recibe el nombre del grupo
// y un array de jugadores ya ordenado (apodo, nombre_real, is_admin).
export const buildJugadoresModalHTML = (nombreGrupo, jugadores) => {
    const count = jugadores.length;
    const countLabel = `${count} ${count === 1 ? 'jugador' : 'jugadores'}`;
    const rows = count === 0
        ? `<p style="text-align:center;color:var(--text-muted);font-size:.85rem;margin:1rem 0;">No hay jugadores cargados.</p>`
        : jugadores.map((j) => `
            <div class="jugadores-modal-row">
                <div class="jugadores-modal-row-main">
                    <strong>${escapeHtml(j.display_name || '—')}</strong>
                    ${j.is_admin ? '<span class="jugadores-modal-admin" title="Admin del grupo">👑</span>' : ''}
                </div>
                ${j.nombre_real ? `<span class="jugadores-modal-row-real">${escapeHtml(j.nombre_real)}</span>` : ''}
            </div>
        `).join('');

    return `
        <div id="${MODAL_ID}" class="jugadores-modal-overlay" role="dialog" aria-modal="true" aria-label="Jugadores del grupo">
            <div class="jugadores-modal-card glass-card">
                <button class="jugadores-modal-close" type="button" aria-label="Cerrar">✕</button>
                <h2 class="jugadores-modal-title">Jugadores de ${escapeHtml(nombreGrupo || 'tu grupo')}</h2>
                <p class="jugadores-modal-count">${countLabel}</p>
                <div class="jugadores-modal-list">${rows}</div>
            </div>
        </div>
    `;
};

const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// CSS inyectado una sola vez al cargar el módulo (idempotente).
const MODAL_CSS = `
    .jugadores-modal-overlay {
        position: fixed; inset: 0; z-index: 95;
        background: rgba(0,0,0,.66); backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 1rem; animation: jugadoresModalIn .18s ease-out;
    }
    @keyframes jugadoresModalIn { from { opacity: 0; } to { opacity: 1; } }
    .jugadores-modal-card {
        position: relative; width: min(100%, 420px); max-height: calc(100dvh - 2rem);
        overflow-y: auto; padding: 1.2rem 1.1rem 1rem; border-radius: 14px;
        background: radial-gradient(circle at 14% 0%, rgba(117,170,219,.10), transparent 38%), rgba(12,18,30,.98);
        border: 1px solid rgba(117,170,219,.2);
    }
    .jugadores-modal-close {
        position: absolute; top: .55rem; right: .65rem;
        width: 30px; height: 30px; border-radius: 999px;
        border: 1px solid rgba(255,255,255,.14); background: rgba(255,255,255,.05);
        color: var(--white); font-size: .85rem; cursor: pointer;
        display: inline-flex; align-items: center; justify-content: center;
    }
    .jugadores-modal-close:hover { background: rgba(255,255,255,.1); }
    .jugadores-modal-title {
        margin: 0 1.8rem .25rem 0; color: var(--white);
        font-family: var(--font-heading); font-size: 1.05rem;
        letter-spacing: .02em; line-height: 1.15;
    }
    .jugadores-modal-count {
        margin: 0 0 .9rem; color: var(--text-muted);
        font-size: .75rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase;
    }
    .jugadores-modal-list { display: flex; flex-direction: column; gap: .42rem; }
    .jugadores-modal-row {
        padding: .55rem .65rem; border-radius: 9px;
        background: rgba(255,255,255,.035); border: 1px solid rgba(255,255,255,.07);
    }
    .jugadores-modal-row-main {
        display: flex; align-items: center; gap: .42rem;
        color: var(--white); font-family: var(--font-heading);
        font-size: .98rem; line-height: 1.1;
    }
    .jugadores-modal-row-main strong { font-weight: 900; }
    .jugadores-modal-admin {
        font-size: .85rem; line-height: 1;
        filter: drop-shadow(0 0 4px rgba(246,196,83,.4));
    }
    .jugadores-modal-row-real {
        display: block; margin-top: .2rem;
        color: var(--text-muted); font-size: .74rem; font-weight: 600;
    }
`;

if (typeof document !== 'undefined' && document.head && typeof document.createElement === 'function') {
    try {
        const STYLE_ID = 'jugadores-modal-css';
        if (!document.getElementById?.(STYLE_ID)) {
            const styleEl = document.createElement('style');
            styleEl.id = STYLE_ID;
            styleEl.textContent = MODAL_CSS;
            document.head.appendChild?.(styleEl);
        }
    } catch { /* polyfills mínimos de test no tienen head completo — no rompemos imports */ }
}

// API pública: abre el modal sobre el DOM. Si ya hay uno abierto, lo cierra.
export const openJugadoresModal = (codigo) => {
    if (typeof document === 'undefined') return;
    closeJugadoresModal();
    if (!codigo) return;
    const db = getNormalizedDB();
    const grupo = db.grupos?.[codigo];
    if (!grupo) return;
    const jugadoresOrdenados = [...(grupo.jugadores || [])].sort((a, b) => {
        const ta = a?.joined_at ? new Date(a.joined_at).getTime() : 0;
        const tb = b?.joined_at ? new Date(b.joined_at).getTime() : 0;
        return ta - tb;
    });
    const container = document.createElement('div');
    container.innerHTML = buildJugadoresModalHTML(getGroupDisplayName(grupo), jugadoresOrdenados);
    const modal = container.firstElementChild;
    document.body.appendChild(modal);
    // Wiring: cierre por X, click en overlay, ESC.
    modal.querySelector('.jugadores-modal-close')?.addEventListener('click', closeJugadoresModal);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeJugadoresModal(); });
    document.addEventListener('keydown', escListener);
};

export const closeJugadoresModal = () => {
    if (typeof document === 'undefined') return;
    document.getElementById(MODAL_ID)?.remove();
    document.removeEventListener('keydown', escListener);
};

const escListener = (e) => { if (e.key === 'Escape') closeJugadoresModal(); };

// Bridge para el onclick="" inline del subtítulo en components.js.
if (typeof window !== 'undefined') {
    window.openJugadoresModal = openJugadoresModal;
    window.closeJugadoresModal = closeJugadoresModal;
}
